import { getRandomString, Logger } from '@saviynt/common';

import { msgs } from './constant';

export function checkEnvironment() {
  return window.envConfig || {};
}

export const getCertsMSconfigFromStore = () =>
  window?.store?.getState()?.user?.certsMSconfig;

export const scrollToPosition = (ref, position) => {
  if (ref.current && typeof ref.current === 'object') {
    ref.current.scroll({
      top: position,
      behavior: 'smooth',
    });
  }
};

export const getStateForPAA = (value) => {
  if (value === 'LIKELY_APPROVAL') {
    return { kind: 'success', label: 'High' };
  }

  if (value === 'POSSIBLE_APPROVAL') {
    return { kind: 'warning', label: 'Medium' };
  }

  if (value === 'POSSIBLE_REJECTION') {
    return { kind: 'critical', label: 'Low' };
  }

  return { kind: 'info', label: '' };
};

export const getTrustChipIconKind = (riskKind) => {
  switch (riskKind) {
    case 'success':
      return 'Happy';
    case 'warning':
      return 'Neutral';
    case 'critical':
      return 'Sad';
    default:
      return 'Sad';
  }
};

export const getRiskMeterParamsForConfidence = (
  score,
  upper = null,
  lower = null,
  middle = null
) => {
  if (typeof score !== 'number')
    return {
      score: 0,
      kind: 'info',
      label: '',
    };

  if (upper && score >= upper) {
    return {
      score,
      kind: 'success',
      label: 'High',
    };
  }

  if (lower && score <= lower) {
    return {
      score,
      kind: 'critical',
      label: 'Low',
    };
  }

  const middleRange = middle.split('-');

  if (
    score >= Number(middleRange[0]) &&
    score <= Number(middleRange[1]) &&
    middle?.trim()?.length !== 0
  ) {
    return {
      score,
      kind: 'warning',
      label: 'Medium',
    };
  }

  return {
    score,
    kind: 'info',
    label: '',
  };
};

// formatted message
export const certsFormattedMessage = (template, values) =>
  template.replace(/{(.*?)}/g, (match, capture) => values[capture] ?? match);

/**
 * Recursively flattens a nested object into a single-level object.
 *
 * @param {Object} object - The object to be flattened. It can contain nested objects.
 * @returns {Object} A new object with all nested properties flattened into a single level.
 */
export const flattenObject = (object) => {
  let temp = {};

  Object.keys(object).forEach((key) => {
    if (typeof object[key] === 'object') {
      temp = { ...temp, ...flattenObject(object[key]) };
    } else {
      temp[key] = object[key];
    }
  });

  return temp;
};

export const flattenList = (nodes) => {
  const tempObj = [];

  nodes.forEach((node) => {
    tempObj.push({ ...node, children: [] });

    if (node?.children && node?.children?.length > 0) {
      tempObj.push(...flattenList(node?.children));
    }
  });

  return tempObj;
};

export const HIERARCHY_KINDS = {
  parent: 'parent', // Only parent items
  children: 'children', // Only immediate children
  both: 'both', // Only parents and immediate children
  bothFull: 'bothFull', // Only parents and all children
  allChildren: 'allChildren', // All children
};

export const findHierarchyElements = (nodes, targetNodeId, find = 'parent') => {
  if (!Object.values(HIERARCHY_KINDS).includes(find)) return [];

  const tempNodes = Array.isArray(nodes) ? nodes : [nodes];
  const nodesList = flattenList(tempNodes);
  const flattenedListLength = nodesList?.length;

  if (flattenedListLength === 0 || flattenedListLength === 1) return [];

  const targetNode = nodesList.find(
    (node) => node?.tracker?.id === targetNodeId
  );

  if (!targetNode) return [];

  const targetNodeLevel = targetNode?.tracker?.level;

  if (find === HIERARCHY_KINDS.parent) {
    if (targetNodeLevel === 0) return [];

    const flattenedList = nodesList.reverse();
    let hasLockedTargetNode = false;
    let currentParentLevel = targetNodeLevel - 1;
    const parentList = [];

    for (let i = 0; i < flattenedListLength; i += 1) {
      if (
        !hasLockedTargetNode &&
        flattenedList[i]?.tracker?.id === targetNodeId
      ) {
        hasLockedTargetNode = true;
      }

      if (
        hasLockedTargetNode &&
        flattenedList[i]?.tracker?.level === currentParentLevel
      ) {
        parentList.push({
          parent: targetNodeLevel - currentParentLevel,
          ...flattenedList[i]?.tracker,
        });
        currentParentLevel -= 1;
      }

      if (currentParentLevel < 0 || parentList.length === targetNodeLevel) {
        return parentList;
      }
    }

    return parentList;
  }

  if (find === HIERARCHY_KINDS.children) {
    const flattenedList = nodesList;
    let hasLockedTargetNode = false;
    let childrenCount = 1;
    const childrenList = [];

    for (let i = 0; i < flattenedListLength; i += 1) {
      if (
        !hasLockedTargetNode &&
        flattenedList[i]?.tracker?.id === targetNodeId
      ) {
        hasLockedTargetNode = true;
      }

      if (
        hasLockedTargetNode &&
        flattenedList[i]?.tracker?.level > targetNodeLevel
      ) {
        childrenList.push({
          children: childrenCount,
          ...flattenedList[i]?.tracker,
        });
        childrenCount += 1;
      }

      if (
        flattenedList[i]?.tracker?.level === targetNodeLevel &&
        flattenedList[i]?.tracker?.id !== targetNodeId
      ) {
        return childrenList;
      }
    }

    return childrenList;
  }

  return [];
};

const flattenListV2 = (nodes, parent = getRandomString()) => {
  const tempObj = [];

  nodes.forEach((node) => {
    tempObj.push({
      ...node,
      tracker: { ...node?.tracker, parentTrackerId: parent },
      children: [],
    });

    if (node?.children && node?.children?.length > 0) {
      tempObj.push(...flattenListV2(node?.children, node?.tracker?.id));
    }
  });

  return tempObj;
};

export const findHierarchyElementsV2 = (
  nodes,
  targetNodeId,
  find = 'parent'
) => {
  if (!Object.values(HIERARCHY_KINDS).includes(find)) return [];

  const tempNodes = Array.isArray(nodes) ? nodes : [nodes];
  const nodesList = flattenListV2(tempNodes);
  const flattenedListLength = nodesList?.length;

  if (flattenedListLength === 0) return [];

  const targetNode = nodesList.find(
    (node) => node?.tracker?.id === targetNodeId
  );

  if (!targetNode) return [];

  const targetNodeLevel = targetNode?.tracker?.level;

  const getParentNode = (id) =>
    nodesList.find((node) => node?.tracker?.id === id);

  if (find === HIERARCHY_KINDS.parent) {
    let childNode = targetNode;
    let currentParentLevel = targetNodeLevel - 1;
    const firstItemSiblings = nodesList.filter(
      (node) =>
        node?.tracker?.parentTrackerId === childNode?.tracker?.parentTrackerId
    );
    let parentList = [
      {
        nodeData: { ...targetNode },
        ...targetNode?.tracker,
        isLastChild:
          firstItemSiblings.length === 1 &&
          firstItemSiblings[0].tracker?.id === childNode?.tracker?.id,
      },
    ];

    while (currentParentLevel >= 0) {
      const parentNode = getParentNode(childNode?.tracker?.parentTrackerId);

      if (parentNode) {
        const siblings = nodesList.filter(
          (node) =>
            node?.tracker?.parentTrackerId ===
            parentNode?.tracker?.parentTrackerId
        );

        parentList = [
          {
            nodeData: { ...parentNode },
            ...parentNode?.tracker,
            isLastChild:
              siblings.length === 1 &&
              siblings[0].tracker?.id === parentNode?.tracker?.id,
          },
        ].concat(parentList);
        childNode = parentNode;
        currentParentLevel -= 1;
      } else {
        break;
      }
    }

    return parentList;
  }

  if (find === HIERARCHY_KINDS.children) {
    return nodesList
      .filter((node) => node?.tracker?.parentTrackerId === targetNodeId)
      .map((node) => ({
        nodeData: { ...node },
        ...node?.tracker,
      }));
  }

  if (find === HIERARCHY_KINDS.both) {
    let childNode = targetNode;
    let currentParentLevel = targetNodeLevel - 1;
    const firstItemSiblings = nodesList.filter(
      (node) =>
        node?.tracker?.parentTrackerId === childNode?.tracker?.parentTrackerId
    );
    let parentList = [
      {
        nodeData: { ...targetNode },
        ...targetNode?.tracker,
        isLastChild:
          firstItemSiblings.length === 1 &&
          firstItemSiblings[0].tracker?.id === childNode?.tracker?.id,
      },
    ];

    while (currentParentLevel >= 0) {
      const parentNode = getParentNode(childNode?.tracker?.parentTrackerId);

      if (parentNode) {
        const siblings = nodesList.filter(
          (node) =>
            node?.tracker?.parentTrackerId ===
            parentNode?.tracker?.parentTrackerId
        );

        parentList = [
          {
            nodeData: { ...parentNode },
            ...parentNode?.tracker,
            isLastChild:
              siblings.length === 1 &&
              siblings[0].tracker?.id === parentNode?.tracker?.id,
          },
        ].concat(parentList);
        childNode = parentNode;
        currentParentLevel -= 1;
      } else {
        break;
      }
    }

    const childrenList = nodesList
      .filter((node) => node?.tracker?.parentTrackerId === targetNodeId)
      .map((node) => ({
        nodeData: { ...node },
        ...node?.tracker,
      }));

    return [...parentList, ...childrenList];
  }

  if (find === HIERARCHY_KINDS.allChildren) {
    let hasLockedTargetNode = false;
    const combinedList = [
      {
        nodeData: { ...targetNode },
        ...targetNode?.tracker,
      },
    ];

    for (let i = 0; i < flattenedListLength; i += 1) {
      if (!hasLockedTargetNode && nodesList[i]?.tracker?.id === targetNodeId) {
        hasLockedTargetNode = true;
      }

      if (
        hasLockedTargetNode &&
        nodesList[i]?.tracker?.level > targetNodeLevel
      ) {
        combinedList.push({
          nodeData: { ...nodesList[i] },
          ...nodesList[i]?.tracker,
        });
      }

      if (
        hasLockedTargetNode &&
        nodesList[i]?.tracker?.level === targetNodeLevel &&
        nodesList[i]?.tracker?.id !== targetNodeId
      ) {
        return combinedList;
      }
    }

    return combinedList;
  }

  return [];
};

export const getMessageKey = (name) =>
  msgs.certs[name]?.ATTESTATIONS || msgs.certs.DEFAULT.ATTESTATIONS;

export const handleRedirectOnClick = (
  newCertDetailsPageEnabled,
  redirectonURL
) => {
  if (!redirectonURL) {
    Logger.log(
      'URL not valid',
      'newCertDetailsPageEnabled',
      newCertDetailsPageEnabled
    );

    return;
  }

  // Redirect to new user experience, if cert details ux enabled
  window.location.replace(`/ECM/${redirectonURL}`);
};

/**
 * Handles back navigation logic and redirects users based on configuration and access levels.
 *
 * @param {Object} backNavigation - An object containing navigation details.
 * @param {boolean} backNavigation.hasOnlyCertifierLevelAccess - Indicates if the user has only certifier-level access.
 * @param {boolean} backNavigation.consulteeLoggedIn - Indicates if the user is logged in as a consultee.
 * @param {boolean} backNavigation.hasCampaignLevelAccess - Indicated owner or delegate member
 * @param {string} backNavigation.campaignKey - A unique key representing the campaign.
 * @param {string|number} backNavigation.certsListGen2 - A flag (number or string) indicating whether the Neo certification list is enabled.
 * @param {Function} [callBackNeo=() => {}] - A callback function to be executed when Neo list navigation is triggered.
 * @param {Function} [callback=() => {}] - A fallback callback function for older list navigation logic.
 * @param {number} [timeout=null] - Optional timeout in milliseconds for delayed redirection. Must be greater than 100 to take effect.
 *
 * @returns {void}
 *
 * @example
 * // Example usage:
 * executeBackNavigation(
 *   { hasOnlyCertifierLevelAccess: true, hasCampaignLevelAccess: true, consulteeLoggedIn: false 'campaign123', '1' },
 *   () => console.log('Neo navigation callback executed'),
 *   () => console.log('Old navigation callback executed'),
 *   200
 * );
 *
 * @description
 * - If `certsListGen2` is enabled (non-zero or valid integer), the function executes `callBackNeo`.
 * - Redirects are determined based on `backNavigation` properties (`hasOnlyCertifierLevelAccess` or `consulteeLoggedIn` && `hasCampaignLevelAccess`.
 * - If a valid `timeout` is provided (greater than 100), the redirection is delayed.
 * - Handles two sets of redirection logic for Neo and old list pages.
 * - Supports multiple callback functions and conditional redirection based on user access levels.
 */
export const executeBackNavigation = (
  backNavigation,
  callBackNeo = () => {},
  callback = () => {},
  timeout = null
) => {
  // Checking if Neo list is enabled
  if (
    backNavigation?.certsListGen2 &&
    parseInt(backNavigation?.certsListGen2, 10)
  ) {
    callBackNeo();

    const listLeveAccess = Boolean(
      (backNavigation?.hasOnlyCertifierLevelAccess ||
        backNavigation?.consulteeLoggedIn) &&
        !backNavigation?.hasCampaignLevelAccess
    );

    if (listLeveAccess) {
      window.location.replace('/ECMv6/certification/list');

      return;
    }

    // If campaign key is not set then don't do any thing
    if (!backNavigation?.campaignKey) return;

    window.location.replace(
      `/ECMv6/certification/list?campaignKey=${backNavigation?.campaignKey}&type=list`
    );

    return;
  }

  // Old list page redirection
  callback();

  // If timeout redirection is required - only for old version
  if (typeof timeout === 'number' && timeout > 100) {
    setTimeout(() => {
      const listLeveAccess = Boolean(
        (backNavigation?.hasOnlyCertifierLevelAccess ||
          backNavigation?.consulteeLoggedIn) &&
          !backNavigation?.hasCampaignLevelAccess
      );

      if (listLeveAccess) {
        window.location.replace('/ECM/certification/list');

        return;
      }

      // If campaign key is not set then don't do any thing
      if (!backNavigation?.campaignKey) return;

      window.location.replace(
        `/ECM/certification/list?campaignKey=${backNavigation?.campaignKey}&type=list`
      );
    }, timeout);

    return;
  }

  const listLeveAccess = Boolean(
    (backNavigation?.hasOnlyCertifierLevelAccess ||
      backNavigation?.consulteeLoggedIn) &&
      !backNavigation?.hasCampaignLevelAccess
  );

  if (listLeveAccess) {
    window.location.replace('/ECM/certification/list');

    return;
  }

  // If campaign key is not set then don't do any thing
  if (!backNavigation?.campaignKey) return;

  window.location.replace(
    `/ECM/certification/list?campaignKey=${backNavigation?.campaignKey}&type=list`
  );
};
